/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

/**
 * UI category assigned to a parsed release note.
 */
type ChangelogSection = 'new' | 'updates' | 'bugFixes';

/**
 * A single changelog item with its normalized UI category.
 */
type ChangelogNote = {
  /** Original note text without the commit suffix. */
  text: string;
  /** Short Git commit identifier shown alongside the note. */
  commit: string;
  /** Normalized category used by the release notes UI. */
  section: ChangelogSection;
};

/**
 * Parsed production release metadata and notes.
 */
type ChangelogRelease = {
  /** Release version from the Markdown heading. */
  version: string;
  /** Production deployment date from the Markdown heading. */
  date: string;
  /** Full source commit identifier, when present. */
  sourceCommit?: string;
  /** Original Azure artifact version, when the release heading is normalized. */
  artifactVersion?: string;
  /** Artifact publication date, when present. */
  publishedAt?: string;
  /** Production deployment status, when present. */
  productionStatus?: string;
  /** Release notes grouped by their normalized UI category. */
  notes: ChangelogNote[];
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const RELEASE_HEADING = /^## \[([^\]]+)\] - (\d{4}-\d{2}-\d{2})$/;
const METADATA_LINE = /^- (Source commit|Artifact version|Artifact published|Production status):\s*(.+)$/;
const SECTION_HEADING = /^### (.+)$/;
const NOTE_LINE = /^- (.+) \(([0-9a-f]{7,40})\)$/i;
const FEATURE_NOTE = /^feat(?:\(|:)/i;
const BUG_FIX_NOTE = /\b(?:fix|fixes|bug|bugfix)\b/i;
const SECTION_BY_HEADING: Partial<Record<string, ChangelogSection>> = {
  Features: 'new',
  Fixes: 'bugFixes',
};
const METADATA_ASSIGNERS: Record<string, (release: ChangelogRelease, value: string) => void> = {
  'Source commit': (release, value) => {
    release.sourceCommit = value.replaceAll('`', '');
  },
  'Artifact version': (release, value) => {
    release.artifactVersion = value.replaceAll('`', '');
  },
  'Artifact published': (release, value) => {
    release.publishedAt = value;
  },
  'Production status': (release, value) => {
    release.productionStatus = value;
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Internal                                  */
/* -------------------------------------------------------------------------- */

/**
 * Maps generated Markdown headings and historic commit prefixes to UI sections.
 *
 * @param heading - Current Markdown section heading.
 * @param text - Changelog note text used for prefix-based classification.
 * @returns The normalized UI section for the note.
 */
const getSectionForNote = (heading: string, text: string): ChangelogSection => {
  const headingSection = SECTION_BY_HEADING[heading];

  if (headingSection) return headingSection;

  if (FEATURE_NOTE.test(text)) return 'new';

  if (BUG_FIX_NOTE.test(text)) return 'bugFixes';

  return 'updates';
};

/**
 * Copies a recognized Markdown metadata line onto the active release.
 *
 * @param release - Release being populated.
 * @param label - Metadata label from the Markdown line.
 * @param value - Metadata value from the Markdown line.
 */
const parseMetadata = (release: ChangelogRelease, label: string, value: string) => {
  METADATA_ASSIGNERS[label]?.(release, value);
};

/**
 * Parses a release heading into its version and production date.
 *
 * @param line - Markdown line to inspect.
 * @returns Release metadata, or `undefined` when the line is not a valid release heading.
 */
const parseReleaseHeading = (line: string): Pick<ChangelogRelease, 'version' | 'date'> | undefined => {
  const match = line.match(RELEASE_HEADING);
  const [, version, date] = match ?? [];

  return version && date ? { version, date } : undefined;
};

/**
 * Parses a recognized release metadata line.
 *
 * @param line - Markdown line to inspect.
 * @returns Metadata label and value, or `undefined` when the line is not metadata.
 */
const parseMetadataLine = (line: string): { label: string; value: string } | undefined => {
  const match = line.match(METADATA_LINE);
  const [, label, value] = match ?? [];

  return label && value ? { label, value } : undefined;
};

/**
 * Parses a release note section heading.
 *
 * @param line - Markdown line to inspect.
 * @returns Section heading text, or `undefined` when the line is not a section heading.
 */
const parseSectionHeading = (line: string): string | undefined => {
  const match = line.match(SECTION_HEADING);
  return match?.[1];
};

/**
 * Parses a Markdown list item that ends with a short Git commit identifier.
 *
 * @param line - Markdown list item to parse.
 * @param heading - Current Markdown section heading.
 * @returns The parsed note, or `undefined` when the line is not a note.
 */
const parseNote = (line: string, heading: string): ChangelogNote | undefined => {
  const match = line.match(NOTE_LINE);
  if (!match) return undefined;

  const [, text, commit] = match;
  if (!text || !commit) return undefined;

  return {
    text,
    commit,
    section: getSectionForNote(heading, text),
  };
};

/* -------------------------------------------------------------------------- */
/*                               Main utilities                               */
/* -------------------------------------------------------------------------- */

/**
 * Parses the generated production CHANGELOG.md format into typed releases.
 *
 * Unknown lines are ignored so formatting changes outside the supported
 * release structure do not prevent the rest of the changelog from loading.
 *
 * @param markdown - Generated CHANGELOG.md content.
 * @returns Parsed releases in the same newest-first order as the source file.
 */
const parseChangelog = (markdown: string): ChangelogRelease[] => {
  const releases: ChangelogRelease[] = [];
  let currentRelease: ChangelogRelease | undefined;
  let currentHeading = '';

  for (const line of markdown.split(/\r?\n/)) {
    const release = parseReleaseHeading(line);
    if (release) {
      currentRelease = { ...release, notes: [] };
      releases.push(currentRelease);
      currentHeading = '';
      continue;
    }

    if (!currentRelease) {
      continue;
    }

    const metadata = parseMetadataLine(line);
    if (metadata) {
      parseMetadata(currentRelease, metadata.label, metadata.value);
      continue;
    }

    const sectionHeading = parseSectionHeading(line);
    if (sectionHeading) {
      currentHeading = sectionHeading;
      continue;
    }

    const note = parseNote(line, currentHeading);
    if (note) {
      currentRelease.notes.push(note);
    }
  }

  return releases;
};

/**
 * Returns the newest release from the generator's newest-first output.
 *
 * @param releases - Parsed releases ordered newest first.
 * @returns The newest release, or `undefined` when no releases exist.
 */
const getLatestRelease = (releases: ChangelogRelease[]): ChangelogRelease | undefined => releases[0];

/**
 * Groups a release's notes into the display order used by the UI.
 *
 * @param release - Release whose notes should be grouped.
 * @returns Notes grouped under New, Updates, and Bug fixes categories.
 */
const getNotesBySection = (release: ChangelogRelease): Record<ChangelogSection, ChangelogNote[]> => ({
  new: release.notes.filter((note) => note.section === 'new'),
  updates: release.notes.filter((note) => note.section === 'updates'),
  bugFixes: release.notes.filter((note) => note.section === 'bugFixes'),
});

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { ChangelogNote, ChangelogRelease, ChangelogSection };
export { getLatestRelease, getNotesBySection, parseChangelog };
